import { recordConnectionStatusChanged } from '@streem/analytics';
import { contextValues } from '@streem/logger';
import { SubscriptionManager } from '@streem/toolbox';
import DetectRTC from 'detectrtc';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { logger } from '../../util/logger';
import { ARController } from '../controllers/ar_controller';
import { BookmarkController } from '../controllers/bookmark_controller';
import { GeolocationController } from '../controllers/geolocation_controller';
import { InvitationController } from '../controllers/invitation_controller';
import { MediaController } from '../controllers/media_controller/media_controller';
import { NoteController } from '../controllers/notes_controller';
import { PersonalizationController } from '../controllers/personalization_controller';
import { RoomController } from '../controllers/room_controller';
import { StreemshotController } from '../controllers/streemshot_controller';
import { ToolsController } from '../controllers/tools_controller';
import { UserController } from '../controllers/user_controller';
import { RoomWall } from '../wall/room_wall';
import { RoomWallSyncBuilder } from '../wall/wall_sync_builder';
import { CameraController } from '../controllers/camera_controller';
import { DetectionController } from '../controllers/detection_controller';
import { HostController } from '../controllers/host_controller';
import { buildTrackProvider } from '../../util/track_provider_builder';
import { RecordingController } from '../controllers/recording_controller';
export class RoomSession {
    static async buildWallSyncClient(config) {
        return await new RoomWallSyncBuilder(config.roomId, config.localDeviceId, config.localUserId, false).build();
    }
    constructor(config, wallSyncClient) {
        this.config = config;
        this.subscriptionManager = new SubscriptionManager();
        this.requeueFailureSubject = new BehaviorSubject(false);
        this.requeueFailure = this.requeueFailureSubject.asObservable();
        logger.info(`Creating new RoomSession for roomId=${config.roomId}`);
        this.callSettings = config.callSettings;
        this.roomId = config.roomId;
        this.roomWall = new RoomWall({
            ...config,
            alive: true,
            wallSyncClient,
        });
        /** Setup controllers */
        this.streemshots = new StreemshotController(this.controllerConfig);
        this.detections = new DetectionController(this.controllerConfig);
        this.user = new UserController(this.controllerConfig);
        this.invitationController = new InvitationController(this.controllerConfig);
        this.geolocation = new GeolocationController(this.controllerConfig);
        this.bookmarks = new BookmarkController(this.controllerConfig);
        this.personalizations = new PersonalizationController(this.controllerConfig);
        this.room = new RoomController(this.controllerConfig);
        this.note = new NoteController(this.controllerConfig);
        this.camera = new CameraController(this.controllerConfig);
        this.recording = new RecordingController(this.controllerConfig);
        const observables = {
            remoteParticipantCapabilities: this.room.remoteParticipantCapabilities,
        };
        this.media = new MediaController(this.controllerConfig, observables, buildTrackProvider(this.callSettings, observables, config.isHostSession ?? false, this.roomId));
        this.tools = new ToolsController({
            ...this.controllerConfig,
            mediaController: this.media,
        });
        this.ar = new ARController({ ...this.controllerConfig, mediaController: this.media });
        this.hostUser = new HostController(this.controllerConfig);
        // Logs
        logger.setContextValue(contextValues.ROOM_ID, config.roomId.toString());
        logger.setContextValue(contextValues.ROOM_PARTICIPANTS, {
            localUser: config.localUserId.toString(),
            localDeviceId: config.localDeviceId.toString(),
            remoteUser: config.remoteUserId.toString(),
            remoteDeviceId: config.remoteDeviceId
                ? config.remoteDeviceId.toString()
                : 'no device id found',
        });
        this.connectionUnsubscriber = this.monitorConnectionStatus();
        this.monitorLocalUserRoomPerformance();
    }
    get controllerConfig() {
        return {
            roomWall: this.roomWall,
            context: {
                localUserId: this.config.localUserId,
                localDeviceId: this.config.localDeviceId,
                remoteUserId: this.config.remoteUserId,
                remoteDeviceId: this.config.remoteDeviceId,
                roomId: this.config.roomId,
                callSettings: this.config.callSettings,
            },
        };
    }
    /**
     * Client initiated way to end the call.
     */
    async leaveRoom() {
        await this.cleanupBeforeLeaveRoom();
        logger.info('Client Initiated leaveRoom()');
        await this.room.leaveRoom();
        await this.endSession();
    }
    async requeue(companyCode, groupName, reservationSid, transferToGroupName) {
        await this.cleanupBeforeLeaveRoom();
        const success = await this.room.requeue(companyCode, groupName, reservationSid, transferToGroupName);
        if (success) {
            this.requeueFailureSubject.next(false);
            await this.endSession();
        }
        else {
            logger.warn('Requeue failed, session not ended');
            this.requeueFailureSubject.next(true);
        }
    }
    /**
     * Internal clean up of the session
     */
    async endSession() {
        this.subscriptionManager.unsubscribeAll();
        try {
            await this.roomWall.disconnect();
        }
        catch (e) {
            logger.error('Failed to disconnect Wall', e);
        }
        try {
            await this.media.disconnect();
        }
        catch (e) {
            logger.error('Failed to disconnect track controller', e);
        }
        if (this.connectionUnsubscriber) {
            this.connectionUnsubscriber();
        }
        clearTimeout(this.roomPerformanceTimer);
        logger.info(`The Room session has ended ${this.roomId}`);
        logger.removeContextValue(contextValues.ROOM_ID);
        logger.removeContextValue(contextValues.ROOM_PARTICIPANTS);
        logger.removeContextValue(contextValues.ROOM_PERFORMANCE);
        logger.removeContextValue(contextValues.WEBRTC);
        logger.removeContextValue(contextValues.TRACK_PROVIDER);
        logger.removeContextValue(contextValues.AUDIO_TRACK_SETTINGS);
        this.config.onEnd();
    }
    async connect() {
        logger.info(`The Room session was created ${this.roomId}`);
        await this.roomWall.connect();
        await this.user.addLocalUserToWall();
        // This is where we publish the camera subjects this client is capable of sending. This will inherit from whichever RoomSession superclass is instantiated.
        await this.room.sendClientCapabilities(this.constructor.canSendCameraSubjects);
        DetectRTC.load(() => {
            logger.setContextValue(contextValues.WEBRTC, () => DetectRTC);
        });
        this.subscriptionManager.add(this.status.pipe(filter(status => status === 'DISCONNECTED')).subscribe({
            next: async () => {
                logger.info('RoomSessionStatus status changed to DISCONNECTED.  Leaving Room');
                await this.leaveRoom();
            },
            error: error => logger.error('Received an error RoomSessionStatus of DISCONNECTED', error),
        }));
    }
    monitorConnectionStatus() {
        // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection
        const connection = 
        // @ts-ignore Experimental API
        navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (!connection) {
            logger.info('Network Information API not enabled for this browser.');
            return;
        }
        let type = connection.effectiveType;
        const roomId = this.roomId;
        function updateConnectionStatus() {
            logger.info(`Network Connection changed. ${type} -> ${connection.effectiveType} | RTT: ${connection.rtt} | Downlink: ${connection.downlink}`);
            let connectionType;
            switch (connection.type) {
                case 'wifi':
                    connectionType = 'WIFI';
                    break;
                // @ts-ignore this is a valid value. DOM-lib is wrong: https://wicg.github.io/netinfo/
                case 'wimax':
                    connectionType = 'WIFI';
                    break;
                case 'ethernet':
                    connectionType = 'WIFI';
                    break;
                case 'cellular':
                    connectionType = 'CELLULAR';
                    break;
                default:
                    connectionType = 'INVALID';
            }
            recordConnectionStatusChanged(roomId.toString(), connection.rtt, connectionType);
            type = connection.effectiveType;
        }
        connection.addEventListener('change', updateConnectionStatus);
        return () => connection.removeEventListener('change', updateConnectionStatus);
    }
    monitorLocalUserRoomPerformance() {
        const roomPerformance = {
            url: document.location.href,
            referrer: document.referrer,
            cookie: navigator.cookieEnabled ? document.cookie : 'disabled',
            language: navigator.language,
            hasFocus: document.hasFocus(),
            readyState: document.readyState,
            pageAgeInSeconds: Math.round(performance.now() / 1000),
            heap: (() => {
                let heap;
                try {
                    heap =
                        // @ts-ignore Moz does not support performance.memory
                        Math.round(performance.memory.usedJSHeapSize / 1048576).toString() + 'MB';
                }
                catch (error) {
                    heap = 'unknown';
                }
                return heap;
            })(),
            heapPercent: (() => {
                let heapPercent;
                try {
                    heapPercent =
                        Math.round(
                        // @ts-ignore Moz does not support performance.memory
                        (performance.memory.usedJSHeapSize /
                            // @ts-ignore Moz does not support performance.memory
                            performance.memory.jsHeapSizeLimit) *
                            100).toString() + '%';
                }
                catch (error) {
                    heapPercent = 'unknown';
                }
                return heapPercent;
            })(), // Percent of heap used
        };
        logger.setContextValue(contextValues.ROOM_PERFORMANCE, roomPerformance);
        this.roomPerformanceTimer = window.setTimeout(() => this.monitorLocalUserRoomPerformance(), 5000);
    }
    async cleanupBeforeLeaveRoom() {
        try {
            logger.info('cleaning up before leaving room');
            await this.note.cleanupBeforeLeaveRoom();
        }
        catch (error) {
            logger.error(error);
        }
    }
}
RoomSession.canSendCameraSubjects = [];
//# sourceMappingURL=room_session.js.map